import { Box, Chip, Tooltip } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setFilter } from 'app/slice/executive.slice'
import type { ExecutiveState } from 'app/slice/executive.slice'
import type { ExecutiveFilterState, ExecutiveTab } from 'api/executive/executive.types'
import { isFilterEffectiveOnTab } from './filter-config'
import {
  useDoctors,
  useOperativeTypes,
  useProcedures,
  usePromotions,
} from 'features/executive/shared/hooks/use-reference-data'

// Map filter key to its i18n label key (shared with FilterDrawer / ExecutiveFilterBar).
export const LABEL_KEY: Partial<Record<keyof ExecutiveFilterState, string>> = {
  doctorId: 'DOCTOR',
  procedureId: 'PROCEDURE',
  txCategoryId: 'TREATMENT_CATEGORY',
  patientType: 'PATIENT_TYPE',
  appointmentStatus: 'APPOINTMENT_STATUS',
  paymentType: 'PAYMENT_TYPE',
  nationality: 'NATIONALITY',
  sourceChannel: 'SOURCE_CHANNEL',
  promotionId: 'PROMOTION',
}

// Static value→display-label maps for enum-like filters (avoid showing raw
// keys like "new" / "facebook" as chip values).
const STATIC_VALUE_LABEL: Partial<Record<keyof ExecutiveFilterState, Record<string, string>>> = {
  patientType: { new: 'คนไข้ใหม่', existing: 'คนไข้เก่า' },
  // appointmentStatus is resolved via i18n at render time so chips read the
  // same Thai labels as the rest of the system (APPOINTMENT.STATUS.*).
  paymentType: {
    cash: 'เงินสด',
    transfer: 'โอน',
    credit: 'บัตรเครดิต',
    debit: 'บัตรเดบิต',
  },
  sourceChannel: {
    facebook: 'Facebook',
    google: 'Google',
    line: 'LINE OA',
    referral: 'แนะนำ',
    walkin: 'Walk-in',
    other: 'อื่นๆ',
  },
}

interface Props {
  /** Tab being viewed — chips for filters this tab ignores render muted (UR-2609-1158). */
  tab?: ExecutiveTab
}

export default function FilterChips({ tab }: Props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const filters = useSelector((s: { executive: ExecutiveState }) => s.executive.filters)

  // These hooks share the module-level promise cache — once loaded once,
  // subsequent calls return instantly. Used to resolve IDs to names so the
  // chip reads "แพทย์: หมอ A" instead of "แพทย์: 5".
  const { data: doctors } = useDoctors()
  const { data: procedures } = useProcedures()
  const { data: opTypes } = useOperativeTypes()
  const { data: promotions } = usePromotions()

  // Split a slice value into individual tokens regardless of shape.
  // null → []; number → [n]; "1,3,5" → ['1','3','5']; "new" → ['new'].
  const tokens = (raw: unknown): string[] => {
    if (raw == null || raw === '') return []
    if (typeof raw === 'number') return [String(raw)]
    if (typeof raw === 'string') {
      return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0)
    }
    return [String(raw)]
  }

  const resolveSingle = (key: keyof ExecutiveFilterState, token: string): string => {
    // Nationality codes → localised name.
    if (key === 'nationality') {
      return t(`EXECUTIVE.FILTER.NATIONALITY_OPTIONS.${token}`, token)
    }
    // Appointment status → use the same i18n namespace as the appointment
    // list/history so chips read consistently across the app.
    if (key === 'appointmentStatus') {
      return t(`APPOINTMENT.STATUS.${token}`, token)
    }
    // Static enum maps.
    const staticMap = STATIC_VALUE_LABEL[key]
    if (staticMap && token in staticMap) return staticMap[token]
    // Reference-data ID → name lookups.
    const asNum = Number(token)
    if (Number.isFinite(asNum)) {
      if (key === 'doctorId') return doctors.find((d) => d.id === asNum)?.name ?? `#${token}`
      if (key === 'procedureId') return procedures.find((p) => p.id === asNum)?.name ?? `#${token}`
      if (key === 'txCategoryId') return opTypes.find((o) => o.id === asNum)?.name ?? `#${token}`
      if (key === 'promotionId') return promotions.find((p) => p.id === asNum)?.name ?? `#${token}`
    }
    return token
  }

  const resolveValue = (key: keyof ExecutiveFilterState, raw: unknown): string => {
    const list = tokens(raw)
    if (list.length === 0) return ''
    if (list.length === 1) return resolveSingle(key, list[0])
    // Multi-select: show first name + count so chips stay short.
    const first = resolveSingle(key, list[0])
    return `${first} +${list.length - 1}`
  }

  const entries = (Object.keys(LABEL_KEY) as (keyof ExecutiveFilterState)[])
    .filter((k) => filters[k] != null)
    .map((k) => {
      const effective = tab ? isFilterEffectiveOnTab(tab, k) : true
      const desc = t(`EXECUTIVE.FILTER.DESC.${LABEL_KEY[k]}`)
      return {
        key: k,
        label: t(`EXECUTIVE.FILTER.${LABEL_KEY[k]}`),
        value: resolveValue(k, filters[k]),
        effective,
        desc: effective ? desc : `${t('EXECUTIVE.FILTER.INEFFECTIVE_CHIP_TOOLTIP')}\n\n${desc}`,
      }
    })

  if (entries.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
      {entries.map((e) => (
        <Tooltip
          key={e.key}
          title={e.desc}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } } }}
        >
          <Chip
            size="small"
            variant={e.effective ? 'filled' : 'outlined'}
            label={`${e.label}: ${e.value}`}
            onDelete={() => dispatch(setFilter({ key: e.key, value: null as never }))}
            // Dashed + faded = "stored but not applied here" so the user never
            // reads a clinic-wide number as a filtered one.
            sx={e.effective ? undefined : { opacity: 0.6, borderStyle: 'dashed', textDecoration: 'line-through' }}
          />
        </Tooltip>
      ))}
    </Box>
  )
}
